import { AxiosInstance } from 'axios'
import { InjectionKey } from '@vue/composition-api'
import * as Z from 'zod'
import { validateSchema, validateUnknown, validateVoid } from './api.zod'
import { axiosApiResponse } from './api.axios'
import { ApiResponse } from './responses'

export class ConversationsApi {
  constructor(private client: AxiosInstance, private uri: string) {}

  private path(path: string): string {
    return this.uri + path
  }

  ListConversations(): ApiResponse<Conversations> {
    return handleConversationsResponse(this.client.get(this.path(`/`)))
  }

  ListConversationsForUser(userId: UserId): ApiResponse<Conversations> {
    return handleConversationsResponse(
      this.client.get(this.path(`/users/${userId}`))
    )
  }

  ListConversationsByCriteria(criteria): ApiResponse<Conversations> {
    return handleConversationsResponse(
      this.client.get(this.path(`/`), { params: criteria })
    )
  }

  GetConversation(id: ConversationId): ApiResponse<Conversations> {
    return handleConversationsResponse(this.client.get(this.path(`/${id}`)))
  }

  CreateConversation(conversation: NewConversation): ApiResponse<Conversation> {
    return handleConversationResponse(
      this.client.post(this.path(`/`), conversation)
    )
  }

  UpdateConversation(
    id: ConversationId,
    conversation: UpdatedConversation
  ): ApiResponse<unknown> {
    return handleUnknownResponse(
      this.client.put(this.path(`/${id}`), conversation)
    )
  }

  ListLogs(conversationId: ConversationId): ApiResponse<Logs> {
    return handleLogsResponse(
      this.client.get(this.path(`/${conversationId}/logs`))
    )
  }
}

export const ConversationsApiKey: InjectionKey<ConversationsApi> = Symbol()

export type ConversationId = number
export type ClientId = string
export type UserId = string
export type ProjectId = string
export type TeamId = string

export type Conversation = Partial<{
  id: ConversationId
  title: string
  date?: string
  description?: string
  summary?: string | null
  type: string
  language: string
  first_party_label: string
  second_party_label: string
  client_id: ClientId
  client_display_name: string
  owner_id: UserId
  owner_display_name?: string
  project_id: ProjectId
  project_display_name?: string
  delete?: boolean
  deleting?: boolean
  team_id: TeamId
  team_display_name?: string
  status: string
}>

export type Conversations = Array<Partial<Conversation>>

export const conversationSchema = Z.object({
  id: Z.number().int(),
  title: Z.string(),
  description: Z.string().optional(),
  summary: Z.string()
    .optional()
    .nullable(),
  type: Z.string(),
  language: Z.string(),
  first_party_label: Z.string(),
  second_party_label: Z.string(),
  client_id: Z.string(),
  client_display_name: Z.string()
    .optional()
    .nullable(),
  owner_id: Z.string()
    .optional()
    .nullable(),
  owner_display_name: Z.string()
    .optional()
    .nullable(),
  project_id: Z.string()
    .optional()
    .nullable(),
  project_display_name: Z.string(),
  deleting: Z.boolean()
    .optional()
    .nullable(),
  delete: Z.boolean()
    .optional()
    .nullable(),
  team_id: Z.string()
    .optional()
    .nullable(),
  team_display_name: Z.string()
    .nullable()
    .optional()
    .nullable(),
  status: Z.string(),
  date: Z.string()
    .optional()
    .nullable(),
  s3_bucket: Z.string()
    .optional()
    .nullable(),
  s3_key_prefix: Z.string()
    .optional()
    .nullable(),
  jira_id: Z.string()
    .optional()
    .nullable()
})

export const newConversationSchema = Z.object({
  title: Z.string().nonempty('Title is required'),
  description: Z.string().optional(),
  type: Z.string().nonempty('Type is required'),
  language: Z.string().nonempty('Language is required'),
  first_party_label: Z.string().nonempty('Label is required'),
  second_party_label: Z.string().nonempty('Label is required'),
  client_id: Z.string().nonempty('Client is required'),
  client_display_name: Z.string().nonempty('Client is required'),
  owner_id: Z.string().nonempty('Owner is required'),
  owner_display_name: Z.string().nonempty('Owner is required'),
  project_id: Z.string().nonempty('Project is required'),
  project_display_name: Z.string().nonempty('Project is required'),
  team_id: Z.string().nonempty('Team is required'),
  team_display_name: Z.string().nonempty('Team is required'),
  date: Z.string().optional()
})

export type NewConversation = Z.infer<typeof newConversationSchema>

export const validateNewConversation = validateSchema(newConversationSchema)

export const updatedConversationSchema = Z.object({
  title: Z.string().nonempty('Title is required'),
  description: Z.string().optional(),
  type: Z.string().nonempty('Type is required'),
  language: Z.string().nonempty('Language is required'),
  first_party_label: Z.string().nonempty('Label is required'),
  second_party_label: Z.string().nonempty('Label is required'),
  owner_id: Z.string().nonempty('Owner is required'),
  owner_display_name: Z.string().nonempty('Owner is required'),
  project_id: Z.string().nonempty('Project is required'),
  project_display_name: Z.string().nonempty('Project is required'),
  team_id: Z.string().nonempty('Team is required'),
  team_display_name: Z.string().nonempty('Team is required'),
  date: Z.string().optional(),
  status: Z.string()
})

export const logSchema = Z.object({
  id: Z.number(),
  conversation: Z.number(),
  time: Z.string(),
  level: Z.string(),
  message: Z.string()
})

export type Log = Z.infer<typeof logSchema>
export type Logs = Array<Log>

export type UpdatedConversation = Z.infer<typeof updatedConversationSchema>

export const validateUpdatedConversation = validateSchema(
  updatedConversationSchema
)

export const validateConversation = validateSchema(conversationSchema)
export const handleConversationResponse = axiosApiResponse(validateConversation)
export const validateConversations = validateSchema(
  Z.array(conversationSchema.partial())
)
export const handleConversationsResponse = axiosApiResponse(
  validateConversations
)
export const validateLog = validateSchema(logSchema)
export const validateLogs = validateSchema(Z.array(logSchema))
export const handleLogsResponse = axiosApiResponse(validateLogs)

export const handleVoidResponse = axiosApiResponse(validateVoid)
export const handleUnknownResponse = axiosApiResponse(validateUnknown)
